import { c as _c } from "react/compiler-runtime";
import type { Address, BigIntish } from '@morpho-org/blue-sdk';
import type { ApiInputToken, VaultRewards } from '@morpho-org/dapp-core';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { isDefined } from '@morpho-org/morpho-ts';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { getVaultWithPoints } from '@/components/charts/helpers/getPoints';
import { getParsedValue } from '@/helpers/formatters';
import { normalizeRewards } from '@/helpers/rewards';
import { isApyBreakdownInRange, mapVaultApyCardRows } from '@/helpers/vaultApyCardRows';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
interface VaultV2ApyParams {
  vaultV2: {
    address: Address;
    asset: ApiInputToken;
    performanceFee: number;
    managementFee?: number | null;
    netApyExcludingRewards?: number | null;
    netApy?: number | null;
    apyBreakdown?: VaultV2ApyBreakdown | null;
    maxRate: BigIntish | null;
    rewards: VaultRewards[];
  } | undefined;
}

/**
 * Calculates APY metrics for a V2 vault including performance fees and rewards.
 * @param params - Parameters object
 * @param params.vaultV2 - Vault data including address, APY, and rewards
 * @returns Object with native APY, net APY, performance fee, management fee, max rate, points, and rewards
 */
export function getVaultV2Apy({
  vaultV2,
  showPoints
}: VaultV2ApyParams & {
  showPoints?: boolean;
}) {
  const apy = getParsedValue(vaultV2?.netApyExcludingRewards, safeParseNumber);
  const rewards = vaultV2 ? normalizeRewards(vaultV2.rewards) : [];
  const reconciledNetApy = vaultV2?.apyBreakdown && isApyBreakdownInRange(vaultV2.apyBreakdown) && vaultV2.netApy != null ? mapVaultApyCardRows({
    breakdown: vaultV2.apyBreakdown ?? null,
    netApyExcludingRewards: vaultV2.netApyExcludingRewards ?? null,
    netApy: vaultV2.netApy ?? null,
    rewards: rewards.map(reward => Number(reward.apr ?? 0n) / 1e18),
    includesDonations: false,
    isRateCapped: false,
    maxApy: null
  }).netApy : null;
  const netApy = getParsedValue(reconciledNetApy ?? vaultV2?.netApy, safeParseNumber);
  const performanceFee = getParsedValue(vaultV2?.performanceFee, safeParseNumber);

  // A Vault V2's management fee defaults to 0 when unset, but the consumer detail
  // apy endpoint returns `null` for it (the list endpoint and GraphQL coalesce to
  // 0). Coerce null -> 0 so every V2 surface shows the row consistently. Keep
  // `undefined` when there's no vault so V1 (no management fee) still hides it.
  const managementFee = isDefined(vaultV2) ? safeParseNumber(vaultV2.managementFee ?? 0) : undefined;
  const points = showPoints && vaultV2?.asset?.chain?.id ? getVaultWithPoints(vaultV2.asset.chain.id, vaultV2.address, null) : [];
  const maxRate = typeof vaultV2?.maxRate === 'number' ? safeParseNumber(vaultV2.maxRate) : isDefined(vaultV2?.maxRate) ? BigInt(vaultV2.maxRate) : null;
  return {
    apy,
    netApy,
    performanceFee,
    managementFee,
    maxRate,
    points,
    rewards
  };
}

/**
 * Hook that calculates and memoizes V2 vault APY metrics with custom token pricing.
 * @param params - Parameters object
 * @param params.vaultV2 - Vault data
 * @returns Memoized APY metrics object
 */
export function useVaultV2Apy(t0) {
  const $ = _c(3);
  const {
    vaultV2
  } = t0;
  const {
    showPoints
  } = useFeatureFlags();
  let t1;
  if ($[0] !== showPoints || $[1] !== vaultV2) {
    t1 = getVaultV2Apy({
      vaultV2,
      showPoints
    });
    $[0] = showPoints;
    $[1] = vaultV2;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}